import { forwardRef } from "react";
import Button, {
  type TypeStyle,
  type TypeVariant,
} from "../Button/Button";
import styles from "./TableContainer.module.css";

interface TableColumn {
  key: string;
  titulo: string | React.ReactNode;
  classStyle?: string;
}

interface TableButton {
  variant?: TypeVariant;
  typeStyle?: TypeStyle | TypeStyle[];
  type?: "reset" | "submit" | "reset" | "button" | undefined;
  title?: string;
  onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  customElement?: React.ReactNode;
}

interface TableContent {
  values: any;
  buttons: TableButton[];
  onClick?: () => void;
  onMouseDown?: (event: React.MouseEvent<HTMLTableRowElement>) => void;
}

interface TableContainerProps {
  columns: TableColumn[];
  content: TableContent[];
  classTrHead?: string;
  classContainer?: string;
  responsive?: boolean;
  tbodyRef?: React.Ref<HTMLTableSectionElement>;
}

const TableContainer = forwardRef<HTMLTableElement, TableContainerProps>(
  (
    { columns, content, classTrHead, classContainer, responsive = true, tbodyRef },
    ref,
  ) => (
    <table
      ref={ref}
      className={`${styles.table} ${responsive && styles.responsive} ${classContainer}`}
    >
      <thead className={styles.tableHead}>
        <tr className={classTrHead}>
          {columns.map((col) => (
            <th key={col.key} className={col.classStyle}>
              {col.titulo}
            </th>
          ))}
        </tr>
      </thead>
      <tbody ref={tbodyRef} className={styles.tableList}>
        {content.length > 0 ? (
          content.map((row, rowIndex) => {
            const { values, buttons, onClick, onMouseDown } = row;

            return (
              <tr
                key={rowIndex}
                onClick={onClick}
                onMouseDown={onMouseDown}
                style={{ cursor: onClick || onMouseDown ? "pointer" : "default" }}
              >
                {columns.map((col) => {
                  if (col.key != "actionEx") {
                    return (
                      <td key={col.key} className={col.classStyle}>
                        {responsive && (
                          <span className={`${styles.title} mobile`}>
                            {col.titulo}
                          </span>
                        )}
                        {values[col.key] ?? "-"}
                      </td>
                    );
                  }

                  if (col.key === "actionEx") {
                    return (
                      <td key={col.key} className={col.classStyle}>
                        {responsive && (
                          <span className={`${styles.title} mobile`}>
                            Ações
                          </span>
                        )}
                        {buttons?.map((btn, btnIndex) =>
                          // ButtonSlide
                          btn.customElement ? (
                            <div
                              key={btnIndex}
                              onClick={(e) => e.stopPropagation()}
                              style={{ display: "inline-block" }}
                            >
                              {btn.customElement}
                            </div>
                          ) : (
                            // Botão comum
                            <Button
                              key={btnIndex}
                              variant={btn.variant!}
                              typeStyle={btn.typeStyle}
                              type={btn.type}
                              title={btn.title!}
                              onClick={(e) => {
                                e.stopPropagation();
                                btn.onClick?.(e);
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                btn.onMouseDown?.(e);
                              }}
                              disabled={btn.disabled}
                            >
                              {responsive && (
                                <span className="mobile">{btn.title}</span>
                              )}
                              {btn.icon}
                            </Button>
                          ),
                        )}
                      </td>
                    );
                  }
                })}
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan={columns.length} style={{ textAlign: "center", flex: 1 }}>
              Nenhum dado encontrado!
            </td>
          </tr>
        )}
      </tbody>
    </table>
  ),
);

TableContainer.displayName = "TableContainer";
export default TableContainer;
